// tslint:disable: no-console no-var-requires no-switch-case-fall-through
import { MarkdownCell } from "@jupyterlab/cells";
import { CellType } from "@jupyterlab/nbformat";
import { KernelMessage } from "@jupyterlab/services";
import { isLocalStorageAvailable, wait } from "@kaggle/core";
import { Kaggle, featureFlags } from "@kaggle/sdk";
import { ThemeName } from "@kaggle/styled-components";
// @ts-ignore
import Postmate from "postmate";
import {
  EDITOR_INIT_FINISHED_ACTION_KEY,
  EDITOR_READY_ACTION_KEY,
  REDUX_ACTION_KEY,
  SAVE_IPYNB_ACTION_KEY,
} from "../../components/editor/SessionlessEditor";
import { sessionStateSubscriber } from "../../connector/channels/sessionStateChannel";
import { FileManager } from "../../jupyter/api-interceptor/api/contents/filemanager";
import { getContext } from "../../jupyter/api-interceptor/api/context";
import { registerJupyterApiRoutes } from "../../jupyter/api-interceptor/api/router";
import {
  createJupyterLabNotebookClient,
  JupyterLabNotebookClient,
} from "../../jupyter/services/createJupyterLabClient";
import { VerticalDirection } from "../../jupyter/services/enums";
import { JupyterEvent } from "../../jupyter/types";
import { setCellState, setJupyterStatus } from "../../redux/actions/jupyter";
import { createSetSessionStateAction } from "../../redux/actions/session";
import { getSessionState } from "../../redux/selectors/session";
import { createFrameStore } from "../../redux/store/createFrameStore";
import { JupyterStatus } from "../../redux/types";
import { CodeLocation } from "../../types/CodeLocation";
import { renderShadowDOM } from "./renderShadowDOM";
import { renderStyle } from "./renderStyle";
import { getJupyterLab } from "./utils/getJupyterLab";
import { registerMarkdownToolbars } from "./utils/registerMarkdownToolbars";
import { whenExists, whenSet } from "./utils/whenExists";
require("../../../static/css/editor.css");
require("../../../static/fonts/editor-fonts.css");

import EditorType = Kaggle.Sdk.Kernels.EditorType;

enum JupyterLabThemeName {
  LIGHT = "JupyterLab Light",
  DARK = "JupyterLab Dark",
}

// JupyterLab loads a popup when you try to reload the Notebook Editor saying "Reload site?" "Changes you made may not be saved".
// This is because we've taken over saving the notebook for JuptyerLab and it still thinks the notebook changes have not been saved.
// To prevent this popup from loading, we capture the beforeunload event that JupyterLab listens for, and skip actually adding the
// listener for that particular function.
const stopJupyterLabUnloadPopup = () => {
  const original = window.addEventListener;
  window.addEventListener = (
    type: any,
    fn: (this: Window, ev: any) => any,
    options: boolean | AddEventListenerOptions | undefined
  ) => {
    if (type === "beforeunload" && fn.toString().includes("status.isDirty")) {
      return;
    }
    original(type, fn, options);
  };
};
stopJupyterLabUnloadPopup();

// tabIsHidden returns true if the tab is currently not in focus.
const tabIsHidden = () => {
  const hiddenKey = "hidden";
  if (hiddenKey in document) return document[hiddenKey];
  const hprops = ["webkit", "moz", "ms", "o"]
    .map(p => p + "Hidden")
    .filter(p => p in document)
    .map(p => (document as any)[p]);
  return hprops.length > 0 ? hprops[0] : false; // Assume not hidden if we couldn't find the hidden property.
};

// JupyterLab uses requestAnimationFrame during load, however on many browsers this
// call is blocked when the tab is not in the foreground. This allows loading
// progress to be made while JupyterLab is backgrounded.
let restoreRequestAnimationFrame = () => {};
const oRequestAnimationFrame = window.requestAnimationFrame;
restoreRequestAnimationFrame = () =>
  (window.requestAnimationFrame = oRequestAnimationFrame);

window.requestAnimationFrame = (callback: any) => {
  if (tabIsHidden()) {
    window.setImmediate(() => callback());
    return 0;
  }
  return oRequestAnimationFrame(callback);
};

// JupyterLab for w/e reason attempts to prevent having the same workspace open
// in multiple tabs. It does this by setting messages on localStorage and listening
// for those messages from other tabs. If it detects the 'same' workspace, it
// redirects the newer one to a 'create new workspace' url, that doesn't exist
// in our static JupyterLab files. Because we always use the 'default' workspace
// this prevents you from opening more than 1 Notebook Editor tab at a time,
// even for different Notebooks (JupyterLab doesn't understand that we can serve)
// different notebooks with the same URL (+token).
// In order to prevent JupyterLab from doing this, we simply override localStorage
// and skip any writes made by JupyterLabs WindowResolver.
// See https://github.com/jupyterlab/jupyterlab/blob/f0153e0258b32674c9aec106383ddf7b618cebab/packages/apputils/src/windowresolver.ts#L67
const stopJupyterLabWorkspaceRedirection = () => {
  if (!isLocalStorageAvailable()) {
    return;
  }
  const originalSetItem = Storage.prototype.setItem;
  Storage.prototype.setItem = (key: string, value: string) => {
    if (key.includes("@jupyterlab/statedb:StateDB")) {
      return;
    }
    originalSetItem(key, value);
  };
};
stopJupyterLabWorkspaceRedirection();

// setWSURL modifies the JupyterLab websocket URL setting, setting it to the
// desired value and then re-triggering connection setup to the new url.
const setWSURL = async (baseUrl: string, kernelName: string) => {
  // Convert to websocket protocol in URL.
  const wsUrl = baseUrl.replace(/^http/, "ws");

  // Obtain a setup JupyterLab API serverSettings:
  const jl = await getJupyterLab();

  // Update these values for the jupyterlab-lsp extension, which
  // will reinitialize when the kernel changes.
  jl.updatePageConfig("rootUri", baseUrl);
  jl.updatePageConfig("baseUrl", baseUrl);
  jl.updatePageConfig(
    "virtualDocumentsUri",
    "file:///kaggle/working/__notebook_source__.ipynb"
  );

  // Set the kernel type (python3 or ir):
  const kernelCtx = await whenExists(() => jl.context?.sessionContext, 1000);
  kernelCtx.kernelPreference = { name: kernelName };

  // Force-set the read-only wsUrl property.
  const ss = await whenExists(
    () => jl.app?.serviceManager?.serverSettings,
    1000
  );
  (ss as any).wsUrl = wsUrl;
  (ss as any).baseUrl = baseUrl;

  // Trigger session re-init with the new wsUrl so we connect to the right wsUrl.
  const ctx = await whenExists(() => jl.context?.sessionContext, 1000);

  if (featureFlags.kernelsLspAutocomplete && ctx.session?.kernel?.info) {
    // If there's an existing kernel, the autocomplete extension may be
    // awaiting on kernel.info. However, the _initialize call below will
    // create a new kernel and abandon the existing Promise, leaving the
    // extension blocked. To counteract this, we immediately resolve the
    // Promise that's about to be abandoned, so that the extension can
    // complete initialization and notice the new kernel.
    (ctx.session.kernel as any)._info.resolve({});
  }

  const privateCtx = ctx as any;
  privateCtx.sessionManager._models.clear(); // Clear out old running sessions so we see the new one.
  await privateCtx._initialize();
};

// Promise used to block model change => save draft until
// after the first draft has been loaded.
const modelLoaded = whenSet();

export const bootstrapJupyterLabSessionlessEditor = () => {
  console.log("Bootstrapping JupyterLAB sessionless editor...");

  let cli: JupyterLabNotebookClient;
  let shortcuts: any[];

  // Create a store inside the frame.
  const store = createFrameStore();
  const dispatch = store.dispatch;

  /** Should be executed on DOM body's first availability (prior to paint). */
  const hideFOUT = () => {
    document.body.style.visibility = "hidden";
    document.body.style.background = "transparent";
  };

  /** Should be executed after our styles have painted to make them visible. */
  const showFOUT = () => {
    document.body.style.visibility = "visible";
  };

  /** Handles FOUT (flash of unstyled text) and style rendering at DOMs first availability. */
  const render = () => {
    hideFOUT();
    renderStyle();

    /** The number of frames that have elapsed since DOM body became accessible. */
    let frameCount = 0;

    /**
     * The number of frames to wait before showing the body again.
     * This occurs around frame 40 for me but it varies with CPU state so I've doubled it.
     * Note: There is no browser event / supported specification to event on browser paint.
     * This is preferable to setTimeout because elapsed frames are a better indicator of CPU work
     * than elapsed milliseconds. setTimeout is generally not a very accurate scheduling mechanism.
     * See: https://stackoverflow.com/a/21097655/769871
     */
    const FOUT_FRAME_COUNT_THRESHOLD = 80;

    /** This spins on each animation frame until threshold is hit, then makes body visible. */
    const onFrame = async () => {
      frameCount++;

      // If minimum number of frames have not been hit for paint, wait for next frame.
      if (frameCount < FOUT_FRAME_COUNT_THRESHOLD) {
        window.requestAnimationFrame(onFrame);
        return;
      }

      const jl = await getJupyterLab();

      // WARNING: Currently markdown toolbar relies on double click triggering injection.
      // New markdown cells need to be rendered to allow that to happen (short term
      // band-aid). If this function got passed a reference to the cell, a more cell local
      // fix would be possible but that doesn't seem to be currently supported in JL client.
      /** Runs immediately after a cell is inserted. */
      const onInsertCell = async () => {
        // Render all markdown cells (markdown toolbar is singleton).
        await jl.app.commands.execute("notebook:render-all-markdown");
        await registerMarkdownToolbars();
        // If the new cell is a markdown cell, let's switch it to edit
        // mode so we don't switch to rendered mode. We need to await the
        // previous render so that the toolbar properly loads.
        const nb = jl.notebookTracker.currentWidget?.content;
        if (nb && nb.activeCell?.model?.type === "markdown") {
          const cell = nb.activeCell as MarkdownCell;
          cell.rendered = false;
        }
      };

      // Any additional event handling requirements can be tacked on to this object.
      const handlers = { onInsertCell };

      // Render Kaggle custom elements into JupyterLab notebook via shadow roots.
      const jcli = await whenExists(() => cli, 1000); // Check every 1s for the notebook client.
      renderShadowDOM(jcli, store, jl, handlers);

      // Elapsed frames threshold has been reached, show the body.
      showFOUT();
    };
    window.requestAnimationFrame(onFrame);
  };

  /** Kicks off our style rendering process at body's first availability in DOM. */
  const ready = () => {
    if (!document.body) {
      window.requestAnimationFrame(ready);
      return;
    }

    render();
  };
  window.requestAnimationFrame(ready);

  // Register routes for Jupyter API interception.
  registerJupyterApiRoutes();

  const parentChannelPromise = new Postmate.Model({
    dispatch,
    initialize: makeAsync(async () => {
      console.log("Bootstrap initialization starting...");

      const parentChannel = await parentChannelPromise;

      // Tell the outer-frame that inner frame has loaded.
      parentChannel.emit(EDITOR_READY_ACTION_KEY);

      // Wait for the JupyterLab API to be available, it has to load via a JupyterLab extension.
      const jl = await getJupyterLab();
      const ctx = await whenExists(() => jl.context?.sessionContext, 1000);
      cli = createJupyterLabNotebookClient(jl, () =>
        parentChannel.emit(JupyterEvent.CODE_CELL_EXECUTE)
      );

      // Prevent commandLinker from trying to make links open in JupyterLab.
      // We want links to open in a new page.
      const oConnectNode = jl.app.commandLinker.connectNode;
      jl.app.commandLinker.connectNode = (
        node: HTMLElement,
        command: string,
        args?: any
      ): HTMLElement => {
        if (node.tagName === "A") {
          return node;
        }
        return oConnectNode(node, command, args);
      };

      // Once the notebook model is available and first content has been set, listen for cell updates
      // and pass the updated content to the outer frame on any change.
      modelLoaded.promise
        .then(() => whenExists(() => jl.context?.model, 1000))
        .then(model => {
          restoreRequestAnimationFrame();
          model.contentChanged.connect(() => {
            const hasContent = model.cells.length > 0;
            if (hasContent && !model.readOnly) {
              parentChannel.emit(SAVE_IPYNB_ACTION_KEY);
            }
          });
        });

      // Once the session context is available, listen for changes to the kernel state,
      // if we move into idle/busy that means the backend connection is working and we
      // should signal the outer frame (Change EditorStatus STARTING => RUNNING).
      const sendConnected = () => {
        // Ignore connection events to no kernel, since those mean we are disconnected.
        if (ctx.kernelDisplayName === "No Kernel") {
          return;
        }
        // TODO(herbison): Only do this when actually disconnected.
        // Otherwise, this state change could come because we haven't properly
        // taken down the kernel during a restart (ex. we are Starting a new
        // kernel, but the old one still is alive, this shouldn't trigger CONNECTED
        // until the new one is alive).
        parentChannel.emit(
          REDUX_ACTION_KEY,
          setJupyterStatus(JupyterStatus.CONNECTED)
        );
      };

      // Listen to idle/busy status on the kernel, and use this both
      // as a proxy for knowing the kernel is connected/working and
      // as a way to signel the outer frame of the IDLE/BUSY state
      // so that it can properly update UI icons / keepalives etc.
      ctx.statusChanged.connect((_, status) => {
        switch (status) {
          case "idle":
            parentChannel.emit(JupyterEvent.KERNEL_IDLE);
            sendConnected();
            break;
          case "busy":
            parentChannel.emit(JupyterEvent.KERNEL_BUSY);
            sendConnected();
            break;
        }
      });

      // We might have started in idle/busy (and therefore wouldn't get a trigger to checkConnected).
      // So we should check if we are already connected.
      // So we should poll until we see we are connected.
      whenExists(() => {
        switch (ctx.kernelDisplayStatus) {
          case "idle":
          case "busy":
            sendConnected();
            return true;
        }
        return false;
      }, 1000);

      /** Listen for changes to cell info and update that data on the outer frame. */
      cli.onCellStateChange(cells => {
        parentChannel.emit(REDUX_ACTION_KEY, setCellState(cells));
      });

      // Register all markdown toolbars for existing markdown cells.
      await registerMarkdownToolbars();

      // Render all markdown cells (markdown toolbar is singleton).
      jl.app.commands.execute("notebook:render-all-markdown");

      // WARNING: When page background area clicked, render all markdown. This
      // ensures any click on the background of the page leads to markdown
      // getting rendered.
      const nb = jl.notebookTracker.currentWidget?.content;
      const notebookDOM = nb?.node;
      notebookDOM?.addEventListener("click", e => {
        // This makes it so it will ignore child elements such as cells since
        // we only care about clicks on the background here.
        if (e.target !== e.currentTarget) {
          return;
        }
        jl.app.commands.execute("notebook:render-all-markdown");
      });

      // Listen to JupyterLab commands being executed in order to
      // propagate events to the outer frame that don't exist in JuptyerLab.
      jl.app.commands.commandExecuted.connect(async (_, cmd) => {
        // TODO(herbison): Log all commands to analytics?

        // Anytime cells are changed to markdown, render all cells, then
        // re-register markdown toolbars.
        if (cmd.id === "notebook:change-cell-to-markdown") {
          await jl.app.commands.execute("notebook:render-all-markdown");
          await registerMarkdownToolbars();
          // If the active cell became a markdown cell, let's switch it to edit
          // mode so we don't switch to rendered mode. We need to await the
          // previous render so that the toolbar properly load.
          if (nb && nb.activeCell?.model?.type === "markdown") {
            const cell = nb.activeCell as MarkdownCell;
            cell.rendered = false;
          }
        }

        // Anytime render-all-markdown is called, we reregister markdown toolbar logic.
        if (cmd.id === "notebook:render-all-markdown") {
          await registerMarkdownToolbars();
        }

        // Listen for the delete-cell command so we can signal the outer
        // frame for a chance to undo the delete.
        if (cmd.id === "notebook:delete-cell") {
          parentChannel.emit(JupyterEvent.CELL_DELETE);
        }

        // WARNING: This is a bit hacky. We're listening for any "run" command
        // to be executed in order to allow the outer frame to detect that the
        // inner frame *wants* to run a cell. This is used in the outer frame
        // to decide to reconnect a disconnected session. This seems to be the
        // only signal I could find that triggers before the cell executes
        // vs. other signals that happen *after* the cell executes successfully
        // which won't work if the notebook is disconnected.
        if (cmd.id.includes("notebook:") && cmd.id.includes("run")) {
          parentChannel.emit(JupyterEvent.CODE_CELL_EXECUTE);
        }
      });

      ctx.iopubMessage.connect((_, msg) => {
        // JupyterLab does not have an OutputAdded event to listen to, but we can listen
        // directly to the iopubMessage stream and just observe the output and format
        // it the same way that Vanilla Jupyter does for constructoring an output area JSON:
        // https://github.com/jupyter/notebook/blob/2ba296039aa70afbfd90630fea14bb8461b40410/notebook/static/notebook/js/outputarea.js#L230
        // This makes sure that the outer-frame listener for OutputAdded works for both vanilla and JupyterLab.

        const json = { output_type: msg.header.msg_type } as any;
        let outputAdded = false;
        if (KernelMessage.isStreamMsg(msg)) {
          const stream = msg as KernelMessage.IStreamMsg;
          json.text = stream.content.text;
          json.name = stream.content.name;
          outputAdded = true;
        }
        if (KernelMessage.isExecuteResultMsg(msg)) {
          const executeResult = msg as KernelMessage.IExecuteResultMsg;
          json.execution_count = executeResult.content.execution_count;
          json.data = executeResult.content.data;
          outputAdded = true;
        }
        if (KernelMessage.isDisplayDataMsg(msg)) {
          const displayData = msg as KernelMessage.IDisplayDataMsg;
          json.transient = displayData.content.transient;
          json.data = displayData.content.data;
          json.metadata = displayData.content.metadata;
          outputAdded = true;
        }
        if (KernelMessage.isErrorMsg(msg)) {
          const errorMsg = msg as KernelMessage.IErrorMsg;
          json.ename = errorMsg.content.ename;
          json.evalue = errorMsg.content.evalue;
          json.traceback = errorMsg.content.traceback;
          outputAdded = true;
        }
        if (outputAdded) {
          parentChannel.emit(JupyterEvent.OUTPUT_ADDED, { output: json });
        }
      });

      cli.setAutosaveInterval(0);
      // TODO(herbison): FrameEventHandler? If needed.

      // Open a channel.
      sessionStateSubscriber()(session => {
        // Push the session state from the top frame into the local Redux.
        store.dispatch(createSetSessionStateAction(session));
      });

      // Signal from the inner-frame that bootstrap init has completed.
      parentChannel.emit(EDITOR_INIT_FINISHED_ACTION_KEY);
      console.log("Bootstrap initialization finished.");
    }),
    connectKernel: makeAsync(
      async ({
        proxyUrl,
        kernelName,
      }: {
        proxyUrl: string;
        kernelName: string;
      }) => {
        console.info("INSIDE connectKernel (JL)");

        console.log(`Connecting ${kernelName} kernel through ${proxyUrl}.`);
        getContext().jupyterProxyUrl = proxyUrl;
        await setWSURL(proxyUrl, kernelName);
      }
    ),
    shutdownKernel: makeAsync(async () => {
      const jl = await getJupyterLab();
      const ctx = jl.context?.sessionContext;
      if (cli) {
        cli.clearCellQueue();
      }
      if (ctx && (ctx as any)._pendingKernelName !== "No Kernel") {
        console.log("Shutting down kernel...");
        await ctx.shutdown();
      }
    }),
    lockEditor: makeAsync(async () => {
      console.log("Locking editor...");
      const jl = await getJupyterLab();
      const nb = jl.notebookTracker.currentWidget?.content;
      const model = nb?.model;
      if (model) {
        model.readOnly = true;
        nb?.widgets.forEach(cell => (cell.readOnly = true));
      }
    }),
    setContent: async (content: string) => {
      FileManager.contentResolve(content);
      // Before allowing model saves to start happening make sure we apply the entire content in memory so that
      // there are no incremental cell saves which can cause data loss.
      const jcli = await whenExists(() => cli, 1000);
      jcli.setSource(content);
      modelLoaded.resolve(void 0);
    },
    // TODO(cochamberlain): This is not reliably getting hit 100% of the time. (Top frame calling too early maybe?).
    setTheme: makeAsync(async (themeName: ThemeName) => {
      // Update redux session state with the theme name.
      const sessionState = getSessionState(store.getState());
      const setSessionState = createSetSessionStateAction({
        ...sessionState,
        themeName,
      });
      store.dispatch(setSessionState);

      let themeSynced = false;
      const syncTheme = async () => {
        const jl = await getJupyterLab();
        switch (themeName) {
          case "light":
            if (jl.themeManager.theme === JupyterLabThemeName.LIGHT) {
              console.info("setTheme:light:SYNCED");
              themeSynced = true;
              return;
            }

            console.info("setTheme:light");
            jl.themeManager.setTheme(JupyterLabThemeName.LIGHT);
            return;
          case "dark":
            if (jl.themeManager.theme === JupyterLabThemeName.DARK) {
              console.info("setTheme:dark:SYNCED");
              themeSynced = true;
              return;
            }

            console.info("setTheme:dark");
            jl.themeManager.setTheme(JupyterLabThemeName.DARK);
            return;
        }
      };

      // A race condition exists where theme doesn't get synced up.
      // This retries until the themeManager matches what we want.
      while (!themeSynced) {
        try {
          await syncTheme();
        } catch (err) {
          console.info("setTheme:An error occurred syncing theme.");
          if (err instanceof Error) {
            console.error(err.message);
          }
        }
        await wait(1000);
      }

      console.info(
        "setThemeName:sessionState",
        getSessionState(store.getState())
      );

      // Return the current theme set on the frame. If something went wrong
      // setting the theme, callers will be aware and can retry.
      return (await getJupyterLab()).themeManager.theme ===
        JupyterLabThemeName.DARK
        ? "dark"
        : "light";
    }),
    setSourceType: makeAsync(
      async (sourceType: Kaggle.Sdk.Kernels.EditorType) => {
        // Update redux session state with the source type.
        const sessionState = getSessionState(store.getState());
        const setSessionState = createSetSessionStateAction({
          ...sessionState,
          sourceType,
        });
        store.dispatch(setSessionState);

        // Set a data attribute on the body to allow our CSS to style the page
        // differently per source type.
        document.body.setAttribute("data-kaggle-source-type", sourceType);

        // Load the shortcuts extension and original shortcuts:
        const jl = await getJupyterLab();
        const settings = await jl.registry.load(
          "@jupyterlab/shortcuts-extension:shortcuts"
        );
        if (!shortcuts) {
          shortcuts = settings.get("shortcuts").composite as any[];
        }

        switch (sourceType) {
          case EditorType.NOTEBOOK:
            await settings.set("shortcuts", [
              {
                command: "notebook:execute-selected-code",
                keys: ["Ctrl Shift Enter"],
                selector: ".jp-Notebook.jp-mod-editMode",
              },
              {
                command: "notebook:queue-run-cell",
                keys: ["Ctrl Enter"],
                selector: ".jp-Notebook.jp-mod-editMode",
              },
              {
                command: "notebook:queue-run-cell",
                keys: ["Ctrl Enter"],
                selector: ".jp-Notebook:focus",
              },
              {
                command: "notebook:queue-run-cell-and-insert-below",
                keys: ["Alt Enter"],
                selector: ".jp-Notebook:focus",
              },
              {
                command: "notebook:queue-run-cell-and-insert-below",
                keys: ["Alt Enter"],
                selector: ".jp-Notebook.jp-mod-editMode",
              },
              {
                command: "notebook:queue-run-cell-and-select-next",
                keys: ["Shift Enter"],
                selector: ".jp-Notebook:focus",
              },
              {
                command: "notebook:queue-run-cell-and-select-next",
                keys: ["Shift Enter"],
                selector: ".jp-Notebook.jp-mod-editMode",
              },
              {
                command: "notebook:run-all-cells-stay-editing",
                keys: ["Ctrl Shift Alt Enter"],
                selector: ".jp-Notebook.jp-mod-editMode",
              },
              {
                command: "notebook:run-all-cells-stay-editing",
                keys: ["Ctrl Shift Alt Enter"],
                selector: ".jp-Notebook:focus",
              },
            ]);
            break;

          case EditorType.SCRIPT:
            // Scripts disable all shorcuts except a handful that it adds:
            const disabled = shortcuts.map(x => ({ ...x, disabled: true }));
            // We add each one twice so that it's available in edit/command(focus) modes:
            await settings.set("shortcuts", [
              {
                command: "notebook:queue-run-cell",
                keys: ["Alt Enter"],
                selector: ".jp-Notebook.jp-mod-editMode",
              },
              {
                command: "notebook:queue-run-cell",
                keys: ["Alt Enter"],
                selector: ".jp-Notebook:focus",
              },
              {
                command: "notebook:execute-selected-code",
                keys: ["Ctrl Enter"],
                selector: ".jp-Notebook.jp-mod-editMode",
              },
              {
                command: "notebook:execute-selected-code",
                keys: ["Ctrl Enter"],
                selector: ".jp-Notebook:focus",
              },
              {
                command: "notebook:execute-selected-code",
                keys: ["Shift Enter"],
                selector: ".jp-Notebook.jp-mod-editMode",
              },
              {
                command: "notebook:execute-selected-code",
                keys: ["Shift Enter"],
                selector: ".jp-Notebook:focus",
              },
              ...disabled, // Disabled going last allows you to 'override' a disable by putting a non-disabled copy above it.
            ]);
            break;
        }
        console.info(
          "setSourceType:sessionState",
          getSessionState(store.getState())
        );
      }
    ),
    undoCellDelete: () => {
      cli.undeleteCell();
    },
    setSource: makeAsync(async (content: string) => {
      return cli.setSource(content);
    }),
    getSource: makeAsync(async () => {
      return cli.getSource();
    }),
    getSourceWithOutputs: makeAsync(async () => {
      return cli.getSourceWithOutputs();
    }),
    deleteCell: makeAsync(async () => {
      return cli.deleteCell(null);
    }),
    execute: makeAsync(async (code: string) => {
      return cli.execute(code);
    }),
    executeAll: makeAsync(async () => {
      return cli.executeAllCells();
    }),
    executeActiveCell: makeAsync(async () => {
      return cli.executeActiveCell();
    }),
    executeCellsAbove: makeAsync(async () => {
      return cli.runAllAbove();
    }),
    executeCellsBelow: makeAsync(async () => {
      return cli.runAllBelow();
    }),
    executeSelectedCode: makeAsync(async () => {
      return cli.executeActiveSelection();
    }),
    cutCell: () => {
      return cli.cutCell();
    },
    copyCell: () => {
      return cli.copyCell();
    },
    pasteCell: () => {
      return cli.pasteCell();
    },
    insertCell: makeAsync(async (type: CellType) => {
      return cli.insertCellBelow(type, null);
    }),
    moveCells: makeAsync(async (direction: string) => {
      return cli.moveActiveCell(
        direction === "up" ? VerticalDirection.UP : VerticalDirection.DOWN
      );
    }),
    stopExecution: makeAsync(async () => {
      return cli.stopExecution();
    }),
    restartKernel: makeAsync(async () => {
      return cli.restartKernel();
    }),
    toCode: makeAsync(async () => {
      return cli.toCode(null);
    }),
    toMarkdown: makeAsync(async () => {
      return cli.toMarkdown(null);
    }),
    toggleShowLineNumbers: makeAsync(async () => {
      return cli.toggleShowLineNumbers();
    }),
    collapseAllCodeCells: makeAsync(async () => {
      return cli.collapseAllCodeCells();
    }),
    expandAllCodeCells: makeAsync(async () => {
      return cli.expandAllCodeCells();
    }),
    toggleOutputScrolling: makeAsync(async () => {
      return cli.toggleOutputScrolling();
    }),
    toggleWordWrap: makeAsync(async () => {
      return cli.toggleWordWrap();
    }),
    navigateToCode: makeAsync(async (codeLocation: CodeLocation) => {
      return cli.navigateToCode(codeLocation);
    }),
    runCommand: makeAsync(async (cmd: string) => {
      return cli.runCommand(cmd, null);
    }),
  });
};

// Adds async call support to Postmate.
// Inspired by https://github.com/dollarshaveclub/postmate/issues/94#issuecomment-533403662
const makeAsync = <U, T>(func: (arg: U | undefined) => Promise<T>) => {
  let cachedArgs: U;
  return (args: U) => {
    if (args !== undefined) {
      cachedArgs = args;
      return undefined;
    } else {
      return func(cachedArgs);
    }
  };
};
